import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { timelineQueryKey } from '@/hooks/use-timeline'
import type { Note, CreateNoteInput } from '@/types/api'

export function notesQueryKey(entityType: string, entityId: string) {
  return ['notes', entityType, entityId] as const
}

export function useNotes(entityType: 'party' | 'opportunity' | 'delivery_project', entityId: string) {
  return useQuery({
    queryKey: notesQueryKey(entityType, entityId),
    queryFn: async () => {
      const data = await api.get<{ notes: Note[] } | Note[]>(
        `/notes?entity_type=${entityType}&entity_id=${entityId}`,
      )
      return Array.isArray(data) ? data : (data as { notes: Note[] }).notes ?? []
    },
    enabled: Boolean(entityId),
  })
}

export function useCreateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateNoteInput) => api.post<Note>('/notes', input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: notesQueryKey(vars.entity_type, vars.entity_id),
      })
      // Notes are one of the three sources the unified Timeline merges —
      // invalidate it too so a posted note shows up without a manual refresh.
      qc.invalidateQueries({
        queryKey: timelineQueryKey(vars.entity_type, vars.entity_id),
      })
    },
  })
}

export function useDeleteNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string; entity_type: string; entity_id: string }) =>
      api.delete<void>(`/notes/${id}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: notesQueryKey(vars.entity_type, vars.entity_id),
      })
      qc.invalidateQueries({
        queryKey: timelineQueryKey(vars.entity_type, vars.entity_id),
      })
    },
  })
}
